import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabConcurrents.module.css';
import MetaHead from '../components/MetaHead';
import { formatNumber } from '../utils/format';

/* ============================================================
   TabConcurrents — onglet "Analyse concurrentielle"
   ------------------------------------------------------------
   IDs DOM préservés pour le backend Express :
     #comp-list           ← conteneur du classement
     #compChart           ← canvas du graphe d'évolution
     #comp-position-rank  ← position #N
     #comp-position-total ← /5
     #comp-rps-score      ← score RPS
     #comp-update-badge   ← badge "live/cache"

   Callbacks (props) :
     - onRefresh()        ← refreshConcurrents()
     - onActionClick(id)  ← navigation vers tab Collecte/Répondre
   ============================================================ */

// ⚠️ NE PAS réutiliser comme valeur par défaut d'une prop : ces données sont
// FICTIVES. Conservées uniquement comme référence de format attendu pour la
// prop `competitors` (voir le container, qui passe les vrais concurrents).
// eslint-disable-next-line no-unused-vars
const FAKE_COMPETITORS_FORMAT_REFERENCE = [
  { rank: 1, name: 'Mon restaurant', emoji: '☕', rating: 4.6, reviews: 312, meta: '312 avis · actif depuis 6 mois avec Revyo', isYou: true },
  { rank: 2, name: 'Chez Fonfon',    emoji: '🍷', rating: 4.4, reviews: 445, meta: '445 avis · sans outil de collecte' },
  { rank: 3, name: 'La Bouillabaisse', emoji: '🐟', rating: 4.3, reviews: 189, meta: '189 avis · en progression' },
  { rank: 4, name: 'Le Miramar',     emoji: '🦞', rating: 4.1, reviews: 208, meta: '208 avis · stagnation depuis 3 mois' },
  { rank: 5, name: 'Pizzeria Roma',  emoji: '🍕', rating: 3.8, reviews: 97,  meta: '97 avis · 3 avis négatifs cette semaine', warn: true },
];

// ⚠️ Séries de DÉMONSTRATION. Le backend ne fournit pas encore d'historique
// d'évolution concurrentielle : tant qu'il n'existe pas, le bloc graphique est
// MASQUÉ (cf. `showChart` plus bas) plutôt que d'afficher à chaque restaurateur
// une courbe de progression 4,3 → 4,6 qui n'est pas la sienne.
// Pour rebrancher : passer les vraies séries en props et retirer la garde.
const YOUR_SERIES   = [4.3, 4.4, 4.4, 4.5, 4.5, 4.6];
const SECTOR_SERIES = [4.2, 4.2, 4.2, 4.3, 4.2, 4.2];

function MiniChart({ months, ariaLabel }) {
  const W = 280, H = 130, P = 16;
  const CHART_MONTHS = months;
  const min = 4.0, max = 4.7;
  const x = (i) => P + (i * (W - P * 2)) / (CHART_MONTHS.length - 1);
  const y = (v) => H - P - ((v - min) / (max - min)) * (H - P * 2);

  const path = (series) =>
    series.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.chartSvg} role="img" aria-label={ariaLabel}>
      {/* gridlines */}
      {[0, 1, 2, 3].map((i) => (
        <line key={i}
          x1={P} x2={W - P}
          y1={P + (i * (H - P * 2)) / 3}
          y2={P + (i * (H - P * 2)) / 3}
          stroke="var(--border)" strokeDasharray="2 4" />
      ))}
      {/* secteur */}
      <path d={path(SECTOR_SERIES)} fill="none" stroke="#AEACA4" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3" />
      {/* vous */}
      <path d={path(YOUR_SERIES)} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {YOUR_SERIES.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r="3.5" fill="white" stroke="var(--accent)" strokeWidth="2" />
      ))}
      {/* labels */}
      {CHART_MONTHS.map((m, i) => (
        <text key={m} x={x(i)} y={H - 2} textAnchor="middle"
          fontSize="9" fill="var(--text3)" fontFamily="JetBrains Mono, monospace">
          {m}
        </text>
      ))}
    </svg>
  );
}

export default function TabConcurrents({
  position = 1,
  total = 5,
  rps = 71,
  // ⚠️ [] et NON des données de démo : passer un défaut fictif ici affichait un
  // classement inventé (restaurants marseillais) à tout resto sans concurrents
  // — même bug que le « hotfix mocks Marseille » du 04/05/2026 sur le dashboard.
  competitors = [],
  strengths,        // si undefined → section masquée
  opportunities,    // si undefined → section masquée
  area,             // ex: "Marseille" — utilisé dans le sous-titre + titre classement
  city,             // ex: "Marseille"
  liveData = false, // affiche le badge "● Données Google en direct"
  // Historique d'évolution vs secteur. Non fourni par le backend à ce jour →
  // le bloc reste masqué tant qu'aucune donnée réelle n'est passée.
  evolutionSeries = null,
  onRefresh = () => {},
  onActionClick = () => {},
}) {
  const { t, i18n } = useTranslation('tabs');
  const lang = i18n.language || 'fr';
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await onRefresh(); } finally { setTimeout(() => setRefreshing(false), 600); }
  };

  /* ===== Calculs dynamiques depuis la liste des concurrents ===== */
  const me = competitors.find(c => c.isYou);
  const others = competitors.filter(c => !c.isYou);
  const sortedByRating = [...competitors].sort((a, b) => b.rating - a.rating);
  const myIdx = sortedByRating.findIndex(c => c.isYou);
  const isFirst = myIdx === 0;
  const competitorAbove = !isFirst ? sortedByRating[myIdx - 1] : null;
  const competitorBelow = isFirst ? sortedByRating[1] : null;

  // Helper : format note Google avec virgule/point selon locale
  const fmtRating = (n) =>
    formatNumber(n, lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  // Avance/retard sur n°2 (ou n°1 si on est second+)
  let avanceLabel = '';
  let avanceValue = '';
  let avanceClass = '';
  if (isFirst && competitorBelow) {
    const diff = (me?.rating || 0) - competitorBelow.rating;
    avanceLabel = t('concurrents.hero_advance_label');
    avanceValue = diff > 0 ? `+${fmtRating(diff)}★` : `0★`;
    avanceClass = styles.heroStatGold;
  } else if (competitorAbove) {
    const diff = competitorAbove.rating - (me?.rating || 0);
    avanceLabel = t('concurrents.hero_delay_label', { rank: myIdx });
    avanceValue = `−${fmtRating(diff)}★`;
    avanceClass = styles.heroStatWarn || styles.heroStatGold;
  }

  // Moyenne d'avis du secteur
  const myReviews = me?.reviews || 0;
  const sectorAvg = others.length > 0
    ? Math.round(others.reduce((s, c) => s + (c.reviews || 0), 0) / others.length)
    : 0;
  const reviewsDiff = myReviews - sectorAvg;

  // Sub-text du hero position (3 branches i18n)
  const positionSub = isFirst
    ? t('concurrents.hero_position_first')
    : myIdx === 1
      ? t('concurrents.hero_position_second')
      : t('concurrents.hero_position_other', { count: myIdx });

  // Sous-titre subtitle (lieu + "mis à jour à l'instant")
  const subtitleParts = [area || city, t('concurrents.subtitle_updated_now')].filter(Boolean);

  // Mois du chart : array i18n (FR par défaut, sinon traduit en EN/ES/IT)
  const chartMonths = t('concurrents.chart_months', { returnObjects: true });
  const safeChartMonths = Array.isArray(chartMonths) && chartMonths.length === 6
    ? chartMonths
    : ['Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'];

  return (
    <div className={styles.wrap}>
      <MetaHead page="concurrents" />
      {/* HEADER */}
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1>
            <span className={styles.titleEmoji}>{t('concurrents.title_emoji')}</span>
            {t('concurrents.title')}
          </h1>
          <p>{subtitleParts.join(' · ')}</p>
        </div>
        <div className={styles.actions}>
          {liveData && (
            <span className={styles.liveBadge} id="comp-update-badge">
              {t('concurrents.live_badge')}
            </span>
          )}
          <button type="button" className={styles.btnGhost} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? t('common.refreshing') : `↻ ${t('common.refresh')}`}
          </button>
        </div>
      </header>

      {/* HERO POSITION */}
      <section className={styles.heroBanner}>
        <div className={styles.heroBlob} aria-hidden="true" />

        <div className={styles.heroPosition}>
          <div className={styles.heroLabel}>{t('concurrents.hero_position_label')}</div>
          <div className={styles.heroRank}>
            <span id="comp-position-rank">#{position}</span>
            <span className={styles.heroTotal}> / <span id="comp-position-total">{total}</span></span>
          </div>
          <div className={styles.heroSub}>{positionSub}</div>
        </div>

        {avanceValue && (
          <>
            <div className={styles.heroDivider} />
            <div>
              <div className={styles.heroLabel}>{avanceLabel}</div>
              <div className={styles.heroStat}>
                <span className={avanceClass}>{avanceValue}</span>
                <span className={styles.heroStatSub}>{t('concurrents.hero_advance_sub')}</span>
              </div>
            </div>
          </>
        )}

        {others.length > 0 && (
          <>
            <div className={styles.heroDivider} />
            <div>
              <div className={styles.heroLabel}>{t('concurrents.hero_reviews_vs_avg_label')}</div>
              <div className={styles.heroStat}>
                <span className={reviewsDiff >= 0 ? styles.heroStatGreen : (styles.heroStatWarn || styles.heroStatGold)}>
                  {reviewsDiff >= 0 ? `+${reviewsDiff}` : reviewsDiff}
                </span>
                <span className={styles.heroStatSub}>{t('concurrents.hero_reviews_vs_avg_sub', { value: sectorAvg })}</span>
              </div>
            </div>
          </>
        )}

        <div className={styles.heroRps}>
          <div className={styles.heroRpsLabel}>{t('concurrents.hero_rps_label')}</div>
          <div className={styles.heroRpsScore} id="comp-rps-score">{rps}</div>
          <div className={styles.heroRpsMax}>/100</div>
        </div>
      </section>

      {/* CLASSEMENT + COL DROITE */}
      <section className={styles.compareGrid}>
        {/* Classement */}
        <article className={styles.card}>
          <header className={styles.cardHeader}>
            <div className={styles.cardTitle}>
              {area
                ? t('concurrents.ranking_title_with_area', { area })
                : t('concurrents.ranking_title')}
            </div>
            <span className={styles.cardBadge}>{t('concurrents.ranking_count', { count: competitors.length })}</span>
          </header>
          <div id="comp-list" className={styles.compList}>
            {competitors.length === 0 && (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon} aria-hidden="true">📍</div>
                <div className={styles.emptyTitle}>{t('concurrents.empty_title')}</div>
                <div className={styles.emptyText}>{t('concurrents.empty_text')}</div>
              </div>
            )}
            {competitors.map((c) => (
              <div
                key={c.rank}
                className={`${styles.compRow} ${c.isYou ? styles.compRowYou : ''} ${c.warn ? styles.compRowWarn : ''}`}
              >
                <div className={`${styles.compRank} ${c.isYou ? styles.compRankYou : ''} ${c.warn ? styles.compRankWarn : ''}`}>
                  #{c.rank}
                </div>
                <div className={styles.compEmoji}>{c.emoji}</div>
                <div className={styles.compInfo}>
                  <div className={styles.compNameRow}>
                    <span className={`${styles.compName} ${c.isYou ? styles.compNameYou : ''}`}>{c.name}</span>
                    {c.isYou && <span className={styles.compTagYou}>{t('concurrents.tag_you')}</span>}
                    {c.isYou && <span className={styles.compTagRevyo}>{t('concurrents.tag_revyo')}</span>}
                  </div>
                  <div className={`${styles.compMeta} ${c.warn ? styles.compMetaWarn : ''}`}>{c.meta}</div>
                </div>
                <div className={styles.compRating}>
                  <div className={`${styles.compRatingValue} ${c.isYou ? styles.compRatingYou : ''} ${c.warn ? styles.compRatingWarn : ''}`}>
                    {fmtRating(c.rating)}
                  </div>
                  <div className={styles.compStars}>
                    {'★'.repeat(Math.round(c.rating))}
                    <span className={styles.compStarsEmpty}>{'★'.repeat(5 - Math.round(c.rating))}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </article>

        {/* Colonne droite : graphe + forces */}
        <div className={styles.rightCol}>
          {/* Le graphe d'évolution n'est rendu QUE si de vraies séries sont
              fournies. Sans cette garde, il traçait YOUR_SERIES/SECTOR_SERIES —
              des constantes de démo — pour tous les restaurants. */}
          {evolutionSeries && (
          <article className={styles.card}>
            <header className={styles.cardHeader}>
              <div className={styles.cardTitle}>{t('concurrents.chart_title')}</div>
              <span className={styles.cardBadge}>{t('concurrents.chart_badge')}</span>
            </header>
            <div className={styles.chartWrap}>
              <MiniChart months={safeChartMonths} ariaLabel={t('concurrents.chart_aria')} />
              <canvas id="compChart" style={{ display: 'none' }} />
              <div className={styles.chartLegend}>
                <div className={styles.chartLegendItem}>
                  <span className={`${styles.chartDash} ${styles.chartDashAccent}`} />
                  {t('concurrents.chart_legend_you')}
                </div>
                <div className={styles.chartLegendItem}>
                  <span className={styles.chartDash} />
                  {t('concurrents.chart_legend_sector')}
                </div>
              </div>
            </div>
          </article>
          )}

          {Array.isArray(strengths) && strengths.length > 0 && (
            <article className={styles.card}>
              <header className={styles.cardHeader}>
                <div className={styles.cardTitle}>{t('concurrents.strengths_title')}</div>
              </header>
              <div className={styles.strengths}>
                {strengths.map((s) => (
                  <div key={s.label}>
                    <div className={styles.strengthHead}>
                      <span className={styles.strengthLabel}>{s.label}</span>
                      <span className={`${styles.strengthBadge} ${s.status === 'best' ? styles.strengthBest : styles.strengthWarn}`}>
                        {s.status === 'best' ? t('concurrents.strengths_best') : t('concurrents.strengths_warn')}
                      </span>
                    </div>
                    <div className={styles.strengthBar}>
                      <div
                        className={`${styles.strengthFill} ${s.status === 'best' ? styles.strengthFillBest : styles.strengthFillWarn}`}
                        style={{ width: `${s.pct}%` }}
                      />
                    </div>
                    <div className={styles.strengthMeta}>
                      <span>{t('concurrents.strengths_you', { value: s.you })}</span>
                      <span>{t('concurrents.strengths_avg', { value: s.avg })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          )}
        </div>
      </section>

      {/* OPPORTUNITÉS IA — affichées seulement si fournies en props */}
      {Array.isArray(opportunities) && opportunities.length > 0 && (
        <section className={styles.card}>
          <header className={styles.cardHeader}>
            <div className={styles.cardTitle}>{t('concurrents.opportunities_title')}</div>
            <span className={styles.cardBadge}>{t('concurrents.opportunities_badge', { count: opportunities.length })}</span>
          </header>
          <div className={styles.oppsGrid}>
            {opportunities.map((opp, i) => {
              const dotClass =
                opp.tone === 'green' || opp.type === 'avantage'   ? styles.oppDotGreen :
                opp.tone === 'amber' || opp.type === 'alerte'     ? styles.oppDotAmber :
                styles.oppDotAccent;
              return (
                <div key={opp.id || i} className={styles.oppItem}>
                  <div className={`${styles.oppDot} ${dotClass}`}>
                    <span className={styles.oppDotIcon}>●</span>
                    <span className={styles.oppDotText}>{opp.label || t('concurrents.opportunities_default_label')}</span>
                  </div>
                  <p>{opp.text || opp.description || ''}</p>
                  {opp.hint && <div className={styles.oppHint}>{opp.hint}</div>}
                  {opp.actionLabel && (
                    <button type="button" className={styles.oppLink} onClick={() => onActionClick(opp.actionId || opp.id)}>
                      → {opp.actionLabel}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
